import { POST_CATEGORY_PAGE_SIZE } from "@/constants/constant"
import httpClient from "@/lib/http-client"
import { Meta, PostData } from "@/types/types"
import { buildUrlWithQuery } from "@/utils/query-string-api-helper"
import { useCallback, useMemo, useRef, useState } from "react"

const CATEGORY_POSTS_POPULATE = {
  image: {
    fields: ["url", "width", "height", "alternativeText"],
  },
  cover: {
    fields: ["url", "width", "height", "alternativeText"],
  },
  categories: {
    fields: ["id", "slug", "name"],
  },
} as const

const CATEGORY_POSTS_FIELDS = [
  "id",
  "title",
  "name",
  "slug",
  "description",
  "excerpt",
  "publishedAt",
  "updatedAt",
  "createdAt",
] as const

export const usePostByCategory = (
  posts: PostData[],
  meta: Meta,
  currentPage: number = 1,
  categorySlug: string = "",
  locale: string = "vi",
  isVideoPage: boolean = false,
) => {
  const isCategoryPage = !isVideoPage && categorySlug && categorySlug !== "search" && categorySlug !== "video"
  
  const [allPostsData, setAllPostsData] = useState<PostData[]>(() => {
    return isCategoryPage ? posts : posts
  })
  
  const [currentClientPage, setCurrentClientPage] = useState(currentPage)
  const [loading, setLoading] = useState(false)
  const [totalPosts, setTotalPosts] = useState(meta.pagination.total || 0)
  const [pageCount, setPageCount] = useState(meta.pagination.pageCount || 0)
  const [fetchedPages, setFetchedPages] = useState<Set<number>>(new Set([1]))
  const postsSectionRef = useRef<HTMLDivElement>(null)

  const fetchMorePosts = useCallback(
    async (page: number) => {
      if (!isCategoryPage || !categorySlug || fetchedPages.has(page)) {
        return
      }

      setLoading(true)
      try {
        const params: Record<string, any> = {
          populate: CATEGORY_POSTS_POPULATE,
          fields: CATEGORY_POSTS_FIELDS,
          sort: "publishedAt:desc",
          publicationState: "live",
          filters: {
            categories: {
              slug: {
                $eq: categorySlug,
              },
            },
          },
          pagination: {
            page: page.toString(),
            pageSize: POST_CATEGORY_PAGE_SIZE.toString(),
          },
          locale,
        }

        const response = await httpClient.get<{ data: PostData[]; meta: Meta }>(buildUrlWithQuery("posts", params))

        if (response?.data) {
          setAllPostsData(prev => {
            const newData = [...prev]
            const startIndex = (page - 1) * POST_CATEGORY_PAGE_SIZE
            response.data.forEach((post, index) => {
              newData[startIndex + index] = post
            })
            return newData
          })
          setFetchedPages(prev => new Set([...prev, page]))
          if (response.meta?.pagination?.total) setTotalPosts(response.meta.pagination.total)
          if (response.meta?.pagination?.pageCount) setPageCount(response.meta.pagination.pageCount)
        }
      } catch (error) {
        console.error("Lỗi khi tải bài viết:", error)
      } finally {
        setLoading(false)
      }
    },
    [isCategoryPage, categorySlug, locale, fetchedPages],
  )

  const displayPosts = allPostsData
  const displayCurrentPage = isCategoryPage ? currentClientPage : currentPage

  const currentPagedPosts = useMemo(() => {
    const startIndex = (displayCurrentPage - 1) * POST_CATEGORY_PAGE_SIZE
    return displayPosts.slice(startIndex, startIndex + POST_CATEGORY_PAGE_SIZE)
  }, [displayPosts, displayCurrentPage])

  const featuredPost = useMemo(() => {
    return currentPagedPosts.length > 0 ? currentPagedPosts[0] : null
  }, [currentPagedPosts])

  const subFeaturePosts = useMemo(() => {
    return currentPagedPosts.length > 1 ? currentPagedPosts.slice(1, 4) : []
  }, [currentPagedPosts])

  const sidePosts = useMemo(() => {
    return currentPagedPosts.length > 4 ? currentPagedPosts.slice(4, 9) : []
  }, [currentPagedPosts])

  const allPosts = useMemo(() => {
    return currentPagedPosts.length > 9 ? currentPagedPosts.slice(9, POST_CATEGORY_PAGE_SIZE) : []
  }, [currentPagedPosts])

  const displayCategoryName = useMemo(() => {
    const cats = displayPosts[0]?.attributes?.categories?.data
    if (!cats?.length) return "Category"
    const match = categorySlug ? cats.find(c => c.attributes?.slug === categorySlug) : null
    return match?.attributes?.name ?? cats[0].attributes.name
  }, [displayPosts, categorySlug])

  const handlePageChange = useCallback(
    async (page: number) => {
      if (!isCategoryPage) return

      if (!fetchedPages.has(page)) {
        await fetchMorePosts(page)
      }

      setCurrentClientPage(page)

      if (postsSectionRef.current) {
        const element = postsSectionRef.current
        const elementPosition = element.getBoundingClientRect().top + window.scrollY
        const offset = 110

        window.scrollTo({
          top: elementPosition - offset,
          behavior: "smooth",
        })
      }
    },
    [isCategoryPage, fetchedPages, fetchMorePosts],
  )

  const displayMeta = {
    ...meta,
    pagination: {
      ...meta.pagination,
      total: totalPosts,
      pageCount: pageCount,
      page: displayCurrentPage,
    },
  }

  return {
    featuredPost,
    subFeaturePosts,
    sidePosts,
    allPosts,
    displayCategoryName,
    displayCurrentPage,
    displayMeta,
    handlePageChange,
    postsSectionRef,
    loading,
  }
}